import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { B2B } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useCoachHandoff, useScrollToQuoteHash } from "@/lib/useQuoteHandoff";
import { B2BTierGrid } from "@/components/b2b/B2BTierGrid";
import { QuoteForm } from "@/components/b2b/QuoteForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  company_name: "", contact_name: "", work_email: "", company_size: "25-100", industry: "",
  interested_in: ["modules"], tier: "team", message: "",
};

export default function B2BSection() {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [fromCoach, setFromCoach] = useState(false);
  useCoachHandoff(setForm, setFromCoach);
  useScrollToQuoteHash();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.work_email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote requested. We'll respond within one business day.");
      setForm((f) => ({ ...f, company_name: "", contact_name: "", work_email: "", industry: "", message: "" }));
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="b2b" data-testid={B2B.section} className="relative bg-[#FAF7F2] py-28 md:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-10 mb-20">
          <div className="col-span-12 md:col-span-6">
            <div className="section-tag mb-6">§ For Companies</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
              License the doctrine for your <span className="italic text-[#B45309]">workforce</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-[#44403C] text-lg leading-relaxed max-w-md">
              Corporate wellness that respects the weight your people already carry. Bulk licenses, live workshops, and manager training — priced to be adopted, not admired.
            </p>
          </div>
        </div>

        <B2BTierGrid userId={userId} onSelect={(tier) => setForm((f) => ({ ...f, tier }))} />

        <div id="quote" className="grid grid-cols-12 mt-24 md:mt-32 gap-y-12">
          <div className="col-span-12 md:col-span-4">
            <div className="section-tag mb-6">§ Request a Quote</div>
            <h3 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight mb-6">
              Bring T.A.C.C. inside your walls.
            </h3>
            <p className="text-[#78716C] leading-relaxed max-w-sm">
              Tell us about your workforce. We respond within one business day with a tailored proposal — no boilerplate, no hidden fees.
            </p>
          </div>
          <QuoteForm form={form} setForm={setForm} fromCoach={fromCoach} loading={loading} onSubmit={submit} />
        </div>
      </div>
    </section>
  );
}
